import { DataTable } from '@/components/features/shared/DataTable'
import { OutstandingAmount } from '@/components/features/shared/OutstandingAmount'
import { Badge } from '@/components/ui/badge'
import { IconButton } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { PaginatedResponse } from '@/domain'
import { ClientSummary, formatClientStatus, getClientFullName, getClientStatusColor } from '@/domain/clients/client'
import { Paths } from '@/routes/paths'
import { useNavigate } from '@tanstack/react-router'
import { ColumnDef, PaginationState } from '@tanstack/react-table'
import { ArrowRight } from 'lucide-react'
import React from 'react'

interface ClientsTableProps {
  paginated: PaginatedResponse<ClientSummary> | undefined
  isLoading?: boolean
  pagination?: PaginationState
  onPaginationChange?: React.Dispatch<React.SetStateAction<PaginationState>>
  onRowClick?: (client: ClientSummary) => void
}

export const ClientsTable = ({
  paginated,
  isLoading,
  pagination,
  onPaginationChange,
  onRowClick,
}: ClientsTableProps) => {
  const navigate = useNavigate()

  const data = paginated?.data || []

  const columns: ColumnDef<ClientSummary>[] = [
    {
      accessorKey: 'name',
      header: 'Nombre',
      cell: ({ row }) => {
        const client = row.original
        return (
          <div className="flex flex-col">
            <span className="font-medium">{getClientFullName(client)}</span>
            <span className="text-sm text-muted-foreground">Cliente N° {client.code}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      cell: ({ row }) => {
        const client = row.original
        return (
          <div className="flex items-center gap-2">
            <Badge className={getClientStatusColor(client.status)}>
              {formatClientStatus(client.status)}
            </Badge>
          </div>
        )
      },
    },
    {
      accessorKey: 'contractsCount',
      header: 'Contratos',
      cell: ({ row }) => {
        const count = row.original.contractsCount
        return (
          <div className="flex items-center gap-2">
            <span className="font-medium text-muted-foreground">{count ?? 0}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'address',
      header: 'Dirección',
      cell: ({ row }) => {
        const address = row.original.address
        return (
          <div className="flex items-center gap-2">
            <span className="font-medium text-muted-foreground">{address || '-'}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'balance',
      header: 'Deuda',
      cell: ({ row }) => (
        <OutstandingAmount balance={row.original.balance} available={(row.original.contractsCount ?? 0) > 0} />
      ),
    },
    {
      id: 'actions',
      header: 'Acciones',
      cell: ({ row }) => {
        const client = row.original
        return (
          <div className="flex items-center gap-2">
            <IconButton
              variant="default"
              title='Ver cliente'
              onClick={() => navigate({ to: `${Paths.Management.Clients}/${client.id}` })}
            >
              <ArrowRight className="h-3.5 w-3.5" />
            </IconButton>
          </div>
        )
      },
    },
  ]

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    )
  }

  return (
    <DataTable
      columns={columns}
      data={data}
      pageCount={paginated?.totalPages}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
      totalRecords={paginated?.total}
      onRowClick={onRowClick}
    />
  )
}
